import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  allCategories,
  filterMovies,
  getCategoryBySlug,
  moviesOfCategory,
  sortMovies,
  sortOptions,
  type SortKey,
} from "@/lib/catalog";

const STEP = 20;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const category = getCategoryBySlug(type);

  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("latest");
  const [visible, setVisible] = useState(STEP);
  const [filterOpen, setFilterOpen] = useState(false);

  const base = useMemo(() => (type ? moviesOfCategory(type) : []), [type]);

  const results = useMemo(() => {
    const filtered = filterMovies(base, {
      years: selectedYears,
      regions: selectedRegions,
      styles: selectedStyles,
    });
    return sortMovies(filtered, sortKey);
  }, [base, selectedYears, selectedRegions, selectedStyles, sortKey]);

  useEffect(() => {
    setVisible(STEP);
  }, [selectedYears, selectedRegions, selectedStyles, sortKey, type]);

  const categoryName = category ? category.name : "未知分类";
  useDocumentMeta({
    title: `${categoryName} - 日本情色电影分类推荐与介绍`,
    description: category
      ? `${category.name}单元收录 ${base.length} 部日本成人文艺电影：${category.summary} 提供年份、地区与风格筛选，帮助快速定位同类作品。`
      : "该分类不存在，请返回首页浏览日本情色电影的其他影像分类。",
    keywords: `${categoryName},日本情色电影,${category ? category.en : "分类"},日影推荐,影片筛选`,
  });

  const toggle = (group: "years" | "regions" | "styles", value: string) => {
    const update = (list: string[]) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
    if (group === "years") setSelectedYears(update);
    if (group === "regions") setSelectedRegions(update);
    if (group === "styles") setSelectedStyles(update);
  };

  const resetAll = () => {
    setSelectedYears([]);
    setSelectedRegions([]);
    setSelectedStyles([]);
  };

  if (!category) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="w-full max-w-lg rounded-lg border border-background-300/60 bg-background-100 p-8 text-center">
            <i className="ri-error-warning-line text-3xl text-accent-500" />
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-50">找不到这个分类</h1>
            <p className="mt-2 text-[13px] leading-relaxed text-foreground-400">
              该分类可能已调整或链接有误。可以回到首页，或从下方入口重新选择。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {allCategories.map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className="cursor-pointer rounded-md border border-background-300/70 px-3 py-1.5 text-[12px] text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400 font-label"
                >
                  {item.name}
                </Link>
              ))}
            </div>
            <Link
              to="/"
              className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-[13px] text-foreground-50 transition-colors hover:bg-primary-600 font-label"
            >
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full border-b border-background-300/50 bg-background-100/50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumb items={[{ label: "分类", to: "/category/classic" }, { label: category.name }]} />
            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
              <div>
                <p className="flex items-center gap-3 text-[11px] tracking-widest2 text-accent-500 font-label">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-primary-700/60 bg-primary-900/30">
                    <i className={`${category.icon} text-sm`} />
                  </span>
                  {category.en.toUpperCase()}
                </p>
                <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-50 md:text-4xl">
                  {category.name}
                </h1>
                <p className="mt-3 max-w-3xl text-[13px] leading-relaxed text-foreground-400">{category.description}</p>
              </div>
              <div className="flex items-center gap-6 lg:justify-end">
                <div>
                  <p className="text-[10px] tracking-widest2 text-foreground-500 font-label">收录影片</p>
                  <p className="mt-1 font-display text-3xl text-accent-400">{String(base.length).padStart(2, "0")}</p>
                </div>
                <div className="h-10 w-px bg-background-400/60" />
                <div>
                  <p className="text-[10px] tracking-widest2 text-foreground-500 font-label">当前结果</p>
                  <p className="mt-1 font-display text-3xl text-foreground-100">
                    {String(results.length).padStart(2, "0")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <button
                type="button"
                onClick={() => setFilterOpen((open) => !open)}
                className="mb-3 flex w-full cursor-pointer items-center justify-between rounded-md border border-background-300/70 px-4 py-2.5 text-[12px] text-foreground-200 lg:hidden font-label"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-accent-500" />
                  筛选条件
                  {selectedYears.length + selectedRegions.length + selectedStyles.length > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] text-foreground-50">
                      {selectedYears.length + selectedRegions.length + selectedStyles.length}
                    </span>
                  )}
                </span>
                <i className={`ri-arrow-down-s-line transition-transform ${filterOpen ? "rotate-180" : ""}`} />
              </button>

              <div className={`${filterOpen ? "block" : "hidden"} lg:block`}>
                <FilterPanel
                  selectedYears={selectedYears}
                  selectedRegions={selectedRegions}
                  selectedStyles={selectedStyles}
                  onToggle={toggle}
                  onReset={resetAll}
                />
              </div>
            </aside>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-300/50 pb-4">
                <p className="text-[12px] text-foreground-500 font-label">
                  共 <span className="text-foreground-100">{results.length}</span> 部影片
                  {results.length > 0 && (
                    <span className="ml-2 text-foreground-600">
                      · 已显示 {Math.min(visible, results.length)} 部
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-1 rounded-full border border-background-300/70 p-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSortKey(option.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[11px] transition-colors font-label ${
                        sortKey === option.key
                          ? "bg-accent-500 text-background-50"
                          : "text-foreground-400 hover:text-foreground-100"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {results.length === 0 ? (
                <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-400/60 bg-background-100/40 px-6 py-16 text-center">
                  <i className="ri-search-line text-3xl text-foreground-600" />
                  <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-100">
                    没有符合条件的影片
                  </h2>
                  <p className="mt-2 max-w-md text-[12px] leading-relaxed text-foreground-500">
                    当前筛选条件组合过于严格。可以尝试减少筛选项，或直接清空条件重新浏览该分类的全部影片。
                  </p>
                  <div className="mt-6 flex flex-wrap justify-center gap-3">
                    <button
                      type="button"
                      onClick={resetAll}
                      className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-[12px] text-foreground-50 transition-colors hover:bg-primary-600 font-label"
                    >
                      清空筛选条件
                    </button>
                    <Link
                      to="/"
                      className="cursor-pointer whitespace-nowrap rounded-md border border-background-300/80 px-5 py-2.5 text-[12px] text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400 font-label"
                    >
                      返回首页
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 xl:grid-cols-4">
                    {results.slice(0, visible).map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>

                  {visible < results.length && (
                    <div className="mt-10 flex flex-col items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setVisible((current) => current + STEP)}
                        className="cursor-pointer whitespace-nowrap rounded-md border border-background-300/80 px-6 py-2.5 text-[12px] text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400 font-label"
                      >
                        加载更多（剩余 {results.length - visible} 部）
                      </button>
                      <span className="text-[11px] text-foreground-600 font-label">
                        已显示 {visible} / {results.length}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}